import { Box, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import Checkbox from '@mui/material/Checkbox';
import React, { RefObject } from 'react'
import { DocumentsType, employmentHistoryType, sectionType } from './Documents';
import { theme } from '../../../../../theme';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { useDeleteDialog } from '../../../../../Context/DeleteConfirmationContext';
import { formatEpochToDate } from '../../../../Common/Date/ConvertDate';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useCandidatePreboarding } from '../CandidatePreboardingContext';
import VisibilityIcon from '@mui/icons-material/Visibility';

interface CandidateDocumentsTableProps {
    documents: DocumentsType[];
    section: sectionType | undefined;
    fileInputRefs: RefObject<{ [key: string]: HTMLInputElement | null }>;
    uploadedFiles: { [key: string]: boolean }; // use string keys now
    selectedFiles: { [key: string]: File | null }; // use string keys
    handleDeleteFile: (customFieldID: number) => void;
    handleAttachClick: (customFieldID: number) => void;
    handleFileChange: (event: React.ChangeEvent<HTMLInputElement>, doc: DocumentsType) => void;
    employmentHistory?: employmentHistoryType[];
    employmentUploadedFiles?: { [key: string]: boolean };
    employmentSelectedFiles?: { [key: string]: File | null };
    handleEmploymentDeleteFile?: (employmentId: number) => void;
    handleEmploymentAttachClick?: (employmentId: number) => void;
    handleEmploymentFileChange?: (event: React.ChangeEvent<HTMLInputElement>, employmentId: number) => void;
    handleConfirmDelete: (documentId: number) => void;
    handleConfirmDeleteEmploymentFile?: (documentId: number) => void;
    uploadOnBehalf: boolean;
    isCandidate: boolean;
    handleDownloadDocument: (documentId: number, documentType: string, isPreview: boolean) => void;
    handleVerifyDocument: (documentId: number, documentType: string, isVerified: boolean) => void;
    documentPreviewForNonUploaded: (id: number, docType: string) => void;
}

const CandidateDocumentsTable: React.FC<CandidateDocumentsTableProps> = ({
    documents,
    section,
    fileInputRefs,
    uploadedFiles,
    selectedFiles,
    handleDeleteFile,
    handleAttachClick,
    handleFileChange,
    employmentHistory,
    employmentUploadedFiles = {},
    employmentSelectedFiles = {},
    handleEmploymentDeleteFile,
    handleEmploymentAttachClick,
    handleEmploymentFileChange,
    handleConfirmDelete,
    handleConfirmDeleteEmploymentFile,
    uploadOnBehalf,
    isCandidate,
    handleDownloadDocument,
    handleVerifyDocument,
    documentPreviewForNonUploaded
}) => {
    const { openDeleteDialog } = useDeleteDialog();
    const isAnyVerified = documents.some(doc => doc.isVerified) || (employmentHistory ? employmentHistory.some(emp => emp.isVerified) : false);
    const isAnyVerificationDate = documents.some(doc => doc.verifiedDate !== null) || (employmentHistory ? employmentHistory.some(emp => emp.verifiedDate !== null) : false);
    const { candidateData } = useCandidatePreboarding();

    return (
        <Box>
            <Typography variant="caption">
                {section?.sectionName}
            </Typography>
            <Paper elevation={1} sx={{ mt: 1, mb: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                <Typography variant='caption'>Document</Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                <Typography variant='caption'>Uploaded</Typography>
                            </TableCell>

                            <TableCell sx={{ textAlign: 'center', width: "20%" }} >
                                <Typography variant='caption'>Uploaded Date</Typography>
                            </TableCell>
                            {
                                ((isAnyVerified || isAnyVerificationDate || !isCandidate) && !uploadOnBehalf) && (
                                    <>
                                        <TableCell sx={{ textAlign: 'center', width: "20%" }} >
                                            <Typography variant='caption'>Verified</Typography>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center', width: "20%" }} >
                                            <Typography variant='caption'>Verification Date</Typography>
                                        </TableCell>
                                    </>
                                )
                            }
                            {
                                (isCandidate || uploadOnBehalf) ? (
                                    <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                        <Typography variant='caption'>Upload</Typography>
                                    </TableCell>
                                ) : (
                                    <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                        <Typography variant='caption'>Download</Typography>
                                    </TableCell>
                                )
                            }

                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {documents
                            .filter(doc => doc.sectionId === section?.sectionId)
                            .map((doc, index) => (
                                <TableRow key={`doc-${doc.customFieldID}`} sx={{ bgcolor: index % 2 !== 0 ? 'inherit' : theme.palette.primary.light }}>
                                    <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                        <Typography variant='body2'>
                                            {doc.fieldName}
                                            {doc.isMandatory && (
                                                <span style={{ color: theme.palette.error.main }}> *</span>
                                            )}
                                        </Typography>

                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                        {/* Uploaded column: show disabled MUI Checkbox, checked if file selected */}
                                        <Checkbox
                                            checked={!!uploadedFiles[`doc-${doc.customFieldID}`]}
                                            disabled
                                            color="primary"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                '&.Mui-checked': {
                                                    color: doc.s3Path ? theme.palette.text.secondary : theme.palette.success.main,
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                        <Typography variant='body2'>
                                            {doc.createdDate ? formatEpochToDate(doc.createdDate) : '-'}
                                        </Typography>
                                    </TableCell>
                                    {((isAnyVerified || isAnyVerificationDate || !isCandidate) && !uploadOnBehalf) && (
                                        <>
                                            <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                                <Checkbox
                                                    checked={doc.isVerified}
                                                    disabled={
                                                        isCandidate ||
                                                        !doc.s3Path ||
                                                        candidateData?.preboardingStatusKey !== "PendingDocumentVerification"
                                                    }
                                                    color="primary"
                                                    onChange={() => handleVerifyDocument(doc.id!, "Candidate Document", !doc.isVerified)}
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        '&.Mui-checked': {
                                                            color: theme.palette.success.main,
                                                        },
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                                <Typography variant='body2'>
                                                    {doc.verifiedDate ? formatEpochToDate(doc.verifiedDate) : '-'}
                                                </Typography>
                                            </TableCell>
                                        </>
                                    )}


                                    {
                                        (isCandidate || uploadOnBehalf) ? (
                                            <TableCell sx={{ textAlign: 'center', width: "20%", p: 0 }}>
                                                <input
                                                    type="file"
                                                    accept=".pdf,.jpg,.jpeg,.png"
                                                    style={{ display: 'none' }}
                                                    ref={(el) => {
                                                        if (fileInputRefs.current) {
                                                            fileInputRefs.current[`doc-${doc.customFieldID}`] = el;
                                                        }
                                                    }}

                                                    onChange={e => handleFileChange(e, doc)}
                                                />

                                                {uploadedFiles[`doc-${doc.customFieldID}`] && (selectedFiles[`doc-${doc.customFieldID}`] || doc.s3Path) ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {
                                                            (doc.isReuploadRequested && (isCandidate || uploadOnBehalf)) && (
                                                                <WarningAmberRoundedIcon fontSize='medium' sx={{ color: theme.palette.error.main, mr: 0.5 }} titleAccess='Re-upload requested' />
                                                            )

                                                        }
                                                        {/* <Typography variant="body2" sx={{ mr: 1, maxWidth: '7vw', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }} title={selectedFiles[`doc-${doc.customFieldID}`]?.name || doc.fileName}>

                                                            {selectedFiles[`doc-${doc.customFieldID}`]?.name || doc.fileName}
                                                        </Typography> */}
                                                        <IconButton
                                                            onClick={() => {
                                                                if (doc.s3Path) {
                                                                    handleDownloadDocument(doc.id!, "Candidate Document", true)
                                                                } else {
                                                                    documentPreviewForNonUploaded(doc.customFieldID, "Candidate Document")
                                                                }
                                                            }}
                                                            title='Preview Document'
                                                        >
                                                            <VisibilityIcon sx={{
                                                                color: theme.palette.primary.main,
                                                            }}

                                                            />
                                                        </IconButton>
                                                        {
                                                            (doc.isReuploadRequested || !doc.isVerified) ? (
                                                                <IconButton onClick={() => {
                                                                    if (doc?.id) {
                                                                        openDeleteDialog(() => handleConfirmDelete(doc.id!), 'Are you sure you want to delete this document?');
                                                                    } else {
                                                                        handleDeleteFile(doc.customFieldID)
                                                                    }
                                                                }} sx={{ color: theme.palette.primary.main }} title='Delete Document'>
                                                                    <i className="fa-solid fa-trash fa-xs"></i>
                                                                </IconButton>
                                                            ) : (
                                                                <></>
                                                            )
                                                        }

                                                    </Box>
                                                ) : (
                                                    <>

                                                        <AttachFileOutlinedIcon
                                                            sx={{ cursor: 'pointer', color: theme.palette.primary.main }}
                                                            onClick={() => handleAttachClick(doc.customFieldID)}
                                                            titleAccess='Upload Document'
                                                        />
                                                        <Tooltip
                                                            title={`Upload ${doc.fieldName} Document (PDF/JPG/PNG, max 2MB)`}
                                                            slotProps={{
                                                                tooltip: {
                                                                    sx: {
                                                                        maxWidth: 180,
                                                                        fontSize: 10,
                                                                        bgcolor: "#fff",         // background white
                                                                        color: "#000",           // text black
                                                                        border: "1px solid #ddd" // optional: subtle border
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <HelpOutlineOutlinedIcon sx={{ cursor: 'pointer', ml: 1, color: theme.palette.primary.main }} />
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </TableCell>
                                        ) :
                                            (
                                                <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                                    {doc.s3Path ? (
                                                        <Box display={'flex'} justifyContent='center' alignItems='center'>
                                                            <IconButton
                                                                onClick={() => { handleDownloadDocument(doc.id!, "Candidate Document", true) }}
                                                                title='Preview Document'
                                                            >
                                                                <VisibilityIcon sx={{
                                                                    color: theme.palette.primary.main,
                                                                }}

                                                                />
                                                            </IconButton>
                                                            <IconButton
                                                                onClick={() => handleDownloadDocument(doc.id!, "Candidate Document", false)}
                                                                sx={{ cursor: 'pointer', color: theme.palette.primary.main }}
                                                                title='Download Document'
                                                            >
                                                                <DownloadIcon />
                                                            </IconButton>


                                                        </Box>


                                                    ) : (
                                                        <Typography variant='body2' color={theme.palette.text.secondary}>-</Typography>
                                                    )}
                                                </TableCell>
                                            )
                                    }

                                </TableRow>
                            ))}

                        {/* Employment History based uploads */}
                        {employmentHistory && employmentHistory.length > 0 && (
                            <>
                                {employmentHistory.map((emp, index) => {
                                    const key = `emp-${emp.employmentId}`;
                                    return (
                                        <TableRow key={emp.employmentId} sx={{ bgcolor: index % 2 !== 0 ? 'inherit' : theme.palette.primary.light }}>
                                            <TableCell sx={{ textAlign: 'center', width: "20%" }}>Experience Letter - {emp.companyName}<span style={{ color: theme.palette.error.main }}> *</span></TableCell>
                                            <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                                <Checkbox
                                                    checked={!!employmentUploadedFiles[key]}
                                                    disabled
                                                    color="primary"
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        '&.Mui-checked': {
                                                            color: emp.s3Path ? theme.palette.text.secondary : theme.palette.success.main,
                                                        },
                                                    }}
                                                />
                                            </TableCell>

                                            <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                                <Typography variant='body2'>
                                                    {emp.createdDate ? formatEpochToDate(emp.createdDate) : '-'}
                                                </Typography>
                                            </TableCell>
                                            {
                                                ((isAnyVerified || isAnyVerificationDate || !isCandidate) && !uploadOnBehalf) && (
                                                    <>
                                                        <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                                            <Checkbox
                                                                checked={emp.isVerified}
                                                                disabled={isCandidate || !emp.s3Path || candidateData?.preboardingStatusKey !== "PendingDocumentVerification"}
                                                                onChange={() => handleVerifyDocument(emp.id!, "Employment Document", !emp.isVerified)}
                                                                color="primary"
                                                                sx={{
                                                                    color: theme.palette.primary.main,
                                                                    '&.Mui-checked': {
                                                                        color: theme.palette.success.main,
                                                                    },
                                                                }}
                                                            />
                                                        </TableCell>

                                                        <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                                            <Typography variant='body2'>
                                                                {emp.verifiedDate ? formatEpochToDate(emp.verifiedDate) : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                    </>
                                                )
                                            }


                                            {
                                                (isCandidate || uploadOnBehalf) ? (
                                                    <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                                        <input
                                                            type="file"
                                                            accept=".pdf,.jpg,.jpeg,.png"
                                                            ref={(el) => {
                                                                if (fileInputRefs.current) {
                                                                    fileInputRefs.current[`emp-${emp.employmentId}`] = el;
                                                                }
                                                            }}
                                                            style={{ display: "none" }}
                                                            onChange={e => handleEmploymentFileChange && handleEmploymentFileChange(e, emp.employmentId)}
                                                        />
                                                        {employmentUploadedFiles[key] && (employmentSelectedFiles[key] || emp.s3Path) ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                {
                                                                    (emp.isReuploadRequested && (isCandidate || uploadOnBehalf)) && (
                                                                        <WarningAmberRoundedIcon fontSize='medium' sx={{ color: theme.palette.error.main, mr: 0.5 }} />
                                                                    )

                                                                }
                                                                {/* <Typography variant="body2" sx={{ mr: 1, maxWidth: '7vw', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }} title={employmentSelectedFiles[key]?.name || emp.fileName}>
                                                                    {employmentSelectedFiles[key]?.name || emp.fileName}
                                                                </Typography> */}
                                                                <IconButton
                                                                    onClick={() => {
                                                                        if (emp.s3Path) {
                                                                            handleDownloadDocument(emp.id!, "Employee Document", true)
                                                                        } else {
                                                                            documentPreviewForNonUploaded(emp.employmentId, "Employee Document")
                                                                        }
                                                                    }}
                                                                    title='Preview Document'
                                                                >
                                                                    <VisibilityIcon sx={{
                                                                        color: theme.palette.primary.main,
                                                                    }}

                                                                    />
                                                                </IconButton>
                                                                {
                                                                    (emp.isReuploadRequested || !emp.isVerified) ? (
                                                                        <IconButton onClick={() => {
                                                                            if (emp?.id) {
                                                                                if (handleConfirmDeleteEmploymentFile) {
                                                                                    openDeleteDialog(() => handleConfirmDeleteEmploymentFile(emp.id!), 'Are you sure you want to delete this document?');
                                                                                }
                                                                            } else {
                                                                                handleEmploymentDeleteFile && handleEmploymentDeleteFile(emp.employmentId)
                                                                            }

                                                                        }} sx={{ color: theme.palette.primary.main }} title='Delete Document'>
                                                                            <i className="fa-solid fa-trash fa-xs"></i>
                                                                        </IconButton>
                                                                    ) : (
                                                                        <></>
                                                                    )
                                                                }

                                                            </Box>
                                                        ) : (
                                                            <>
                                                                <AttachFileOutlinedIcon
                                                                    sx={{ cursor: 'pointer', color: theme.palette.primary.main }}
                                                                    onClick={() => handleEmploymentAttachClick && handleEmploymentAttachClick(emp.employmentId)}
                                                                    titleAccess='Upload Document'
                                                                />
                                                                <Tooltip
                                                                    title={`Upload Experience Letter for ${emp.companyName} (PDF/JPG/PNG, max 2MB)`}
                                                                    slotProps={{
                                                                        tooltip: {
                                                                            sx: {
                                                                                maxWidth: 180,
                                                                                fontSize: 10,
                                                                                bgcolor: "#fff",
                                                                                color: "#000",
                                                                                border: "1px solid #ddd"
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <HelpOutlineOutlinedIcon sx={{ cursor: 'pointer', ml: 1, color: theme.palette.primary.main }} />
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                    </TableCell>
                                                ) :
                                                    (
                                                        <TableCell sx={{ textAlign: 'center', width: "20%" }}>
                                                            {emp.s3Path ? (

                                                                <Box>
                                                                    <IconButton
                                                                        onClick={() => { handleDownloadDocument(emp.id!, "Employment Document", true) }}
                                                                        title='Preview Document'
                                                                    >
                                                                        <VisibilityIcon sx={{
                                                                            color: theme.palette.primary.main,
                                                                        }}

                                                                        />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        onClick={() => handleDownloadDocument(emp.id!, "Employment Document", false)}
                                                                        sx={{ cursor: 'pointer', color: theme.palette.primary.main }}
                                                                        title='Download Document'
                                                                    >
                                                                        <DownloadIcon


                                                                        />
                                                                    </IconButton>
                                                                </Box>


                                                            ) : (
                                                                <Typography variant='body2' color={theme.palette.text.secondary}>-</Typography>
                                                            )}
                                                        </TableCell>
                                                    )
                                            }

                                        </TableRow>
                                    );
                                })}
                            </>
                        )}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    )
}

export default CandidateDocumentsTable